import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  TrendingUp,
  ArrowRight,
  Users,
  Building2,
  Landmark,
  Heart,
  Award,
  Handshake,
  Megaphone,
} from "lucide-react";

const lenses = [
  {
    icon: Users,
    name: "Individual",
    description: "Beneficiary satisfaction, skill improvement, employment outcomes",
    score: 82,
    color: "emerald",
  },
  {
    icon: Landmark,
    name: "Government",
    description: "Policy alignment, cost savings, service gap coverage",
    score: 75,
    color: "blue",
  },
  {
    icon: Building2,
    name: "Business",
    description: "CSR partnerships, volunteer hours, in-kind donations",
    score: 71,
    color: "amber",
  },
  {
    icon: Heart,
    name: "Community",
    description: "Community reach, partnership network, local economic impact",
    score: 84,
    color: "violet",
  },
];

const modules = [
  {
    icon: TrendingUp,
    title: "Impact Framework Builder",
    description: "AI-assisted tool to define, measure, and communicate your social impact across all stakeholder lenses.",
  },
  {
    icon: Handshake,
    title: "Business Partnership Module",
    description: "Connect with businesses for CSR initiatives, data insights, and verified impact certification.",
  },
  {
    icon: Megaphone,
    title: "Advocacy & Milestones",
    description: "Aggregate data for sector advocacy, celebrate organizational milestones, and gain publicity.",
  },
];

export const ImpactGrowPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 gradient-hero" data-testid="impactgrow-hero">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-cyan-100 text-cyan-700 hover:bg-cyan-100 border-0 px-4 py-1.5 text-sm font-medium">
                  <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                  Impact Growth
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-zinc-900 tracking-tight mb-6"
              >
                ImpactGrow
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-8"
              >
                Quantify, communicate, and scale your social impact. Measure outcomes across four stakeholder 
                lenses and build partnerships that multiply your reach.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium px-8"
                    data-testid="impactgrow-cta"
                  >
                    Measure Your Impact
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading font-semibold text-zinc-900">Impact Score</h3>
                    <p className="text-sm text-zinc-500">Overall performance</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-heading font-bold text-cyan-600">78</p>
                    <Badge className="bg-cyan-100 text-cyan-700">Growing</Badge>
                  </div>
                </div>
                <div className="space-y-4">
                  {lenses.map((lens, i) => (
                    <div key={lens.name}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <lens.icon className={`h-4 w-4 text-${lens.color}-600`} />
                          <span className="text-sm font-medium text-zinc-700">{lens.name}</span>
                        </div>
                        <span className="text-sm font-bold text-zinc-900">{lens.score}</span>
                      </div>
                      <Progress value={lens.score} className="h-2" />
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-cyan-50 rounded-xl border border-cyan-100">
                  <p className="text-sm text-cyan-800">
                    <strong>Next step:</strong> Complete business partnership assessment (+12 points)
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4 Lenses Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="lenses-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100 border-0">
              Multi-Perspective Framework
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              Four lenses of impact
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Measure and communicate your social impact from every stakeholder perspective.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lenses.map((lens, index) => (
              <motion.div
                key={lens.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100 hover:border-cyan-200 transition-all text-center"
              >
                <div className={`w-14 h-14 rounded-xl bg-${lens.color}-100 flex items-center justify-center mx-auto mb-4`}>
                  <lens.icon className={`h-7 w-7 text-${lens.color}-600`} />
                </div>
                <h3 className="text-xl font-heading font-semibold text-zinc-900 mb-2">{lens.name}</h3>
                <p className="text-sm text-zinc-600 mb-4">{lens.description}</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-2xl font-heading font-bold text-zinc-900">{lens.score}</span>
                  <span className="text-sm text-zinc-500">/100</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 md:py-32 bg-zinc-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-100 border-0">
              Growth Modules
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              Tools to grow your impact
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {modules.map((module, index) => (
              <motion.div
                key={module.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-zinc-100 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-5">
                  <module.icon className="h-6 w-6 text-cyan-600" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-zinc-900 mb-3">{module.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{module.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-cyan-600">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
            Tell your impact story
          </h2>
          <p className="text-lg text-cyan-100 mb-10 max-w-2xl mx-auto">
            Measure outcomes, build partnerships, and communicate your social impact with confidence.
          </p>
          <Link to="/dashboard">
            <Button
              size="lg"
              className="bg-white text-cyan-700 hover:bg-zinc-100 font-medium px-8 py-6 text-base"
            >
              Start Measuring
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ImpactGrowPage;
